/**
 * Routing shell. Spec §6 flow: Login -> base table (filters + table + selection + export).
 *
 * Auth gate: RequireAuth runs whoami(); spinner while loading; redirect to /login on 401.
 */

import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useSession } from "@/api/hooks";
import { Login } from "./components/Login";
import { AppHeader } from "./components/AppHeader";
import { VolunteersTable } from "./components/VolunteersTable";
import { FacetsPanel } from "./components/FacetsPanel";
import { ResultsHeader } from "./components/ResultsHeader";
import { TouchCart } from "./components/TouchCart";
import { useUrlFilters } from "./hooks/useUrlFilters";
import { usePanelWidth } from "./hooks/usePanelWidth";
import { ActivistCard } from "./components/activists/ActivistCard";
import { UnifiedFileEditPage } from "./components/UnifiedFileEditPage";
import { ScheduledJobsPage } from "./components/jobs/ScheduledJobs";
import { AdminPage } from "./components/AdminPage";
import { Spinner } from "./components/ui/spinner";

/** Gate: requires an authenticated session, else redirects to /login. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const session = useSession();

  if (session.isPending) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (session.isError) {
    const status = session.error instanceof ApiError ? session.error.status : 0;
    if (status === 401 || status === 403 || status === 0) {
      // Preserve the full location (search + hash), so ?f= filters and page/sort
      // survive the round-trip through /login (Xfe-5).
      const from = location.pathname + location.search + location.hash;
      return <Navigate to="/login" replace state={{ from }} />;
    }
    // Non-auth error: surface it rather than bouncing into a login loop.
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-2 text-sm text-destructive">
        <p>Не удалось загрузить сессию.</p>
        <p className="text-muted-foreground">
          {session.error instanceof Error ? session.error.message : "Неизвестная ошибка"}
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * The authenticated "База" screen — 3-column shell (design handoff, mockup_2):
 *   left   258–560px  facets (#F7F9FC, border-r) — ресайз за правую ручку,
 *                     ширина сохраняется в localStorage (usePanelWidth);
 *                     двойной клик по ручке возвращает 258px
 *   center flex       results (ResultsHeader + VolunteersTable)
 *   right  312px      touch cart (#fff, border-l)
 */
function BasePage() {
  // Sync filters/page/pageSize/sort <-> URL so reload / shared links restore the view.
  useUrlFilters();
  const { width, startResize, resetWidth } = usePanelWidth();
  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <AppHeader />
      <div className="flex min-h-0 flex-1">
        {/* Внешний контейнер НЕ скроллится (inset-y-0 у ручки корректен);
            скроллится внутренний div с самой панелью. */}
        <div className="relative shrink-0" style={{ width }}>
          <div
            className="h-full overflow-y-auto border-r bg-[var(--panel-subtle)]"
            style={{ borderColor: "var(--border-hex, #E2E8F0)" }}
          >
            <FacetsPanel />
          </div>
          {/* Ручка-разделитель: drag = ресайз, двойной клик = сброс к 258px.
              Tailwind v3 молча игнорирует alpha-модификатор на var() (bg-[var(...)]/30),
              поэтому подсветка = непрозрачный accent + opacity элемента (ручка пустая). */}
          <div
            onPointerDown={startResize}
            onDoubleClick={resetWidth}
            className="absolute inset-y-0 right-0 z-10 w-[5px] cursor-col-resize hover:bg-[var(--accent-hex,#2563EB)] hover:opacity-30"
            aria-hidden
          />
        </div>
        <main className="flex min-w-0 flex-1 flex-col">
          <ResultsHeader />
          <div className="min-h-0 flex-1 overflow-hidden">
            <VolunteersTable />
          </div>
        </main>
        <aside
          className="flex w-[312px] shrink-0 overflow-hidden border-l bg-[var(--panel)]"
          style={{ borderColor: "var(--border-hex, #E2E8F0)" }}
        >
          <TouchCart />
        </aside>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <BasePage />
          </RequireAuth>
        }
      />
      <Route
        path="/activists/:id"
        element={
          <RequireAuth>
            <ActivistCard />
          </RequireAuth>
        }
      />
      <Route
        path="/import"
        element={
          <RequireAuth>
            <UnifiedFileEditPage />
          </RequireAuth>
        }
      />
      {/* Legacy path: the standalone "Готовность" import page was folded into
          /import ("Обновление из файла"). Redirect old bookmarks. */}
      <Route path="/readiness" element={<Navigate to="/import" replace />} />
      <Route
        path="/scheduled"
        element={
          <RequireAuth>
            <ScheduledJobsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <AdminPage />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
